import useLog from '../../utils/log/useLog';
import useAppConfig from 'canvas_states/config/useAppConfig';
import { useEffect, useState } from 'react';
import ErrorStatus from '../../../../types/errors/ErrorStatus';
import WarningStatus from '../../../../types/errors/WarningStatus';

/**
 * Side effect only hook that handle the initialization of third party analytic script that will
 * be loaded into iframes. The hook will ensure that only when the iframe is present and fully loaded,
 * the GA event queue will be published in order to the partner. After the initial push, the hook will
 * set an interval and push real-time events.
 * @see https://plusgrade.jira.com/wiki/spaces/FRON/pages/4311941122/TDS+3rd+Party+Tagging+in+Canvas
 */

export interface PartnerTagManagerProviderProps {
  children: React.ReactNode;
}
const getIframeTagManager = () => document.getElementById('partner_tag_manager') as HTMLIFrameElement;

const getUrlNoQueryParam = (url: string) => {
  const noQueryParamUrl = new URL(url);
  noQueryParamUrl.search = '';
  return noQueryParamUrl;
};

const INTERVAL_DELAY = 2000;

const PartnerTagManagerProvider: React.FC<PartnerTagManagerProviderProps> = ({ children }) => {
  const [initialEventsSent, setInitialEventsSent] = useState(false);
  const partnerConfig = useAppConfig().states.config;
  const partnerTagManagerEnabled = partnerConfig?.canvas?.partnerTagManager?.enabled;
  const { logWarn, logError } = useLog().actions;
  const postAllEvents = () => {
    const dataLayer = window.thirdPartyIframeDataLayer ?? [];
    let nextEvent = dataLayer.shift();
    while (nextEvent) {
      postIframeMessage(nextEvent);
      nextEvent = dataLayer.shift();
    }
  };

  const postIframeMessage = (fullPayload: object) => {
    const iframe = getIframeTagManager();
    try {
      if (iframe?.contentWindow && partnerTagManagerEnabled) {
        const strippedUrl = getUrlNoQueryParam(iframe.src).toString();
        if (partnerConfig?.canvas?.partnerTagManager?.url === strippedUrl) {
          iframe.contentWindow.postMessage(fullPayload, iframe.src);
        } else {
          logWarn(
            WarningStatus.UNEXPECTED_PARAMETER,
            `PartnerTagManager is not enabled or the URL "${iframe.src}" is not whitelisted.`,
            'PartnerTagManager',
          );
        }
      }
    } catch (error) {
      logError(ErrorStatus.PARTNER_TAG_MANAGER, error);
    }
  };

  useEffect(() => {
    const handler = (ev: MessageEvent) => {
      if (!ev.data || !partnerTagManagerEnabled) return;
      if (ev.data === partnerConfig?.canvas?.partnerTagManager?.loadedEvent) {
        postAllEvents();
        window.removeEventListener('message', handler);
        setInitialEventsSent(true);
      }
    };
    window.addEventListener('message', handler);
    // Remove addEventListener when component unmounted
    return () => window.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    let interval = null;
    if (partnerTagManagerEnabled) {
      interval = setInterval(() => {
        if (initialEventsSent) {
          postAllEvents();
        }
      }, INTERVAL_DELAY);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [initialEventsSent]);
  return <>{children}</>;
};

export default PartnerTagManagerProvider;
